import AbstractAuthenticatedService from '@vertical-plus/vue-js-micro-service-components/lib/model/service/abstract-authenticated-service';
import FormFieldOptionInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-option-interface';
import GetServiceTask from '@/model/task/billing/get-service-task';
import GetServicesTask from '@/model/task/billing/get-services-task';
import PatchServiceTask from '@/model/task/billing/patch-service-task';
import PostServiceTask from '@/model/task/billing/post-service-task';
import Service from '@/model/entity/billing/service';
import ServiceSearchConditionsInterface from '@/model/vo/billing/service-search-conditions-interface';
import ServiceSearchResultsVo from '@/model/vo/billing/service-search-results-vo';

export default class ServiceService extends AbstractAuthenticatedService
{
    /**
     * Returns matching services
     *
     * @returns
     */

    public async findServices(searchConditions?: ServiceSearchConditionsInterface): Promise<ServiceSearchResultsVo>
    {
        const task = new GetServicesTask();
        task.token = this.token;
        task.searchConditions = searchConditions;

        return this.runTask(task) as Promise<ServiceSearchResultsVo>;
    }

    /**
     * Loads a service
     *
     * @param id
     * @returns
     */

    public async loadService(id: string): Promise<Service>
    {
        const task = new GetServiceTask();
        task.token = this.token;
        task.id = id;

        return this.runTask(task) as Promise<Service>;
    }

    /**
     * Creates a service
     *
     * @param service
     * @returns
     */

    public async createService(service: Service): Promise<Service>
    {
        const task = new PostServiceTask();
        task.token = this.token;
        task.service = service;

        return this.runTask(task) as Promise<Service>;
    }

    /**
     * Updates a service
     *
     * @param service
     * @returns
     */

    public async updateService(service: Service): Promise<Service>
    {
        const task = new PatchServiceTask();
        task.token = this.token;
        task.service = service;

        return this.runTask(task) as Promise<Service>;
    }

    /**
     * Callback function for form options
     *
     * @param search
     * @param clientId
     * @param itemsPerPage
     * @returns
     */

    public async serviceFormOptionsCallback(
        search: string,
        clientId: number|null = null,
        itemsPerPage: number|null = null,
    ): Promise<FormFieldOptionInterface[]>
    {
        const services = await this.findServices({
            searchTerm: search,
            client: clientId ?? undefined,
            disabled: false,
            itemsPerPage: itemsPerPage ?? undefined,
            order: [
                {
                    key: 'name',
                    descending: false,
                },
            ],
        });

        const options: FormFieldOptionInterface[] = [];

        for (const service of services.results)
        {
            options.push({
                value: service.id,
                title: service.title,
            });
        }

        return options;
    }

    /**
     * Callback function for form titles
     *
     * @param values
     * @returns
     */

    public async serviceFormTitlesCallback(values: string[]): Promise<Record<string, string>>
    {
        const services = await this.findServices({
            id: values,
            order: [
                {
                    key: 'name',
                    descending: false,
                },
            ],
        });

        const options: Record<string, string> = {};

        for (const service of services.results)
        {
            if (!service.id)
            {
                continue;
            }

            options[service.id] = service.title;
        }

        return options;
    }
}